// src/pages/Servicios/TurnoDetalle.jsx
import { Box, Typography, Button, Grid, Chip, Stack, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../../../components/Header";
import { fetchServices, toggleCancelService } from "../../../services/serviceService";
import { getDriverById } from "../../../services/driverService";
import { getTurnoDetalle, resolveTurnoCredit } from "../../../services/turnoService";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTheme, alpha } from "@mui/material/styles";
import { useMemo, useState, useEffect, useCallback } from "react";

/** ========== Paletas ========== */
const PALETTE_DARK = {
  cardBg: "#1E293B",
  cardBorder: "#334155",
  text: "#F1F5F9",
  label: "#94A3B8",
  title: "#E2E8F0",
  accent: "#38BDF8",
  btn: "#3B82F6",
  btnHover: "#2563EB",
};

const PALETTE_LIGHT = {
  cardBg: "#ECECEC",
  cardBorder: "#E5E7EB",
  text: "#1E293B",
  label: "#475569",
  title: "#111827",
  accent: "#38BDF8",
  btn: "#2563EB",
  btnHover: "#1D4ED8",
};

const mapService = (svc) => ({
  SERVICIO: svc._id || svc.externalId,
  "FECHA INICIO": svc.fechaInicio ? new Date(svc.fechaInicio).toLocaleString() : "",
  "FECHA FIN": svc.fechaFin ? new Date(svc.fechaFin).toLocaleString() : "",
  "MÉTODO DE PAGO": svc.metodoPago,
  "TIPO DE SERVICIO": svc.tipoServicio,
  "DISTANCIA km.": svc.distanciaKm,
  "DIRECCIÓN INICIO": svc.direccionInicio,
  "DIRECCIÓN FIN": svc.direccionFin,
  "TOTAL EUR": svc.importes?.total ?? svc.importeTotal ?? 0,
  cancelado: Boolean(svc.cancelado),
});

const resolveLicenseLabel = (license) => {
  if (!license) return "—";
  if (typeof license === "object") {
    return license.licenseNumber || license.number || license.license || license._id || "—";
  }
  return license;
};

/** ========== Reutilizables ========== */
function DataChip({ label, value, palette, highlight = false, compact = false }) {
  return (
    <Box
      sx={{
        p: compact ? 1.25 : 2,
        borderRadius: "10px",
        backgroundColor: palette.cardBg,
        border: `1px solid ${highlight ? palette.accent : palette.cardBorder}`,
        minHeight: compact ? 56 : 76,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 6px 18px rgba(2, 6, 23, 0.08)"
            : "0 2px 6px rgba(0,0,0,0.20)",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: palette.label,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          mb: compact ? 0.25 : 0.5,
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: palette.text,
          fontWeight: 700,
          fontSize: compact ? 16 : 20,
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function InfoCard({ title, children, palette }) {
  return (
    <Box
      sx={{
        backgroundColor: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: "10px",
        p: 2,
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 6px 18px rgba(2, 6, 23, 0.08)"
            : "0 2px 6px rgba(0,0,0,0.20)",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          color: palette.label,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          mb: 1.5,
          fontWeight: 700,
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function RowItem({ label, value, palette }) {
  return (
    <Box
      sx={{
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: "10px",
        p: 1.5,
      }}
    >
      <Typography variant="body2" sx={{ color: palette.label, mb: 0.5 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          color: palette.text,
          fontWeight: 600,
          fontSize: { xs: "0.95rem", sm: "1rem" },
          wordBreak: "break-word",
          overflowWrap: "anywhere",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/** Contenedor con scroll propio para la columna izquierda */
function FitColumn({ children }) {
  return (
    <Box
      sx={{
        height: "100%",      // ocupa toda la altura disponible
        minHeight: 0,
        overflowY: "auto",   // scroll vertical para no comprimir tarjetas
        overflowX: "hidden",
        pr: 1,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        pb: 2,
      }}
    >
      {children}
    </Box>
  );
}

/** ========== Página ========== */
const TurnoDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const palette = useMemo(() => (isDark ? PALETTE_DARK : PALETTE_LIGHT), [isDark]);
  const isMobile = useMediaQuery("(max-width:900px)");
  const [services, setServices] = useState([]);
  const [servicesError, setServicesError] = useState("");
  const [loadingServices, setLoadingServices] = useState(false);
  const [mutatingServiceId, setMutatingServiceId] = useState(null);
  const [mutationError, setMutationError] = useState("");
  const [closureMeta, setClosureMeta] = useState(null);
  const [driverName, setDriverName] = useState("");
  const [driverPct, setDriverPct] = useState(null);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [resolvingCredit, setResolvingCredit] = useState(false);
  const [showDiscrepancyModal, setShowDiscrepancyModal] = useState(false);
  const [hasDiscrepancy, setHasDiscrepancy] = useState(false);

  const loadData = useCallback(async () => {
    setLoadingServices(true);
    setServicesError("");
    setMutationError("");
    try {
      const detail = await getTurnoDetalle(id);
      setClosureMeta(detail?.turno || detail?.closure || null);
      const driverIdFromDetail = detail?.turno?.driver?._id || detail?.turno?.driverId || detail?.closure?.driver?._id || detail?.closure?.driverId;
      if (driverIdFromDetail) {
        try {
          const drv = await getDriverById(driverIdFromDetail);
          setDriverName(drv?.name || drv?.fullName || drv?.username || "");
          if (Number.isFinite(drv?.revenuePercentage)) {
            setDriverPct(drv.revenuePercentage);
          }
        } catch (err) {
          console.log("[WEB][TurnoDetalle] no se pudo cargar nombre del conductor", err?.message);
        }
      } else {
        setDriverName(detail?.closure?.driver?.name || detail?.closure?.driverName || "");
      }
      console.log("[WEB][TurnoDetalle] solicitando servicios por cierre", id);
      const data = await fetchServices({ closureId: id });
      console.log("[WEB][TurnoDetalle] servicios recibidos", data?.length);
      setServices(data.map(mapService));

      const creditDiscrepancy = detail?.turno?.creditDiscrepancy;
      setShowCreditModal(Boolean(creditDiscrepancy));
    } catch (e) {
      console.error("[WEB][TurnoDetalle] error cargando servicios", e);
      setServicesError(e.message || "No se pudieron cargar los servicios");
    } finally {
      setLoadingServices(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totalServicios = useMemo(
    () => services.reduce((sum, svc) => sum + Number(svc["TOTAL EUR"] ?? 0), 0),
    [services]
  );

  const recaudacionTomar = Number(closureMeta?.startIncome ?? closureMeta?.recaudacionAlTomar ?? 0);
  const recaudacionDejar = Number(closureMeta?.endIncome ?? closureMeta?.recaudacionAlDejar ?? 0);
  const tieneRecaudacion =
    Number.isFinite(recaudacionTomar) &&
    Number.isFinite(recaudacionDejar) &&
    (recaudacionTomar !== 0 || recaudacionDejar !== 0 || totalServicios === 0);
  const facturacionDesdeRecaudacion = tieneRecaudacion ? recaudacionDejar - recaudacionTomar : null;
  const facturacionDesdeServicios = Number.isFinite(totalServicios) ? totalServicios : 0;
  const facturacionElegida = Number(
    (tieneRecaudacion ? facturacionDesdeRecaudacion : facturacionDesdeServicios).toFixed(2)
  );
  const hayDiscrepancia =
    tieneRecaudacion &&
    services.length > 0 &&
    Math.abs((facturacionDesdeRecaudacion ?? 0) - facturacionDesdeServicios) > 0.01;
  useEffect(() => {
    setHasDiscrepancy(hayDiscrepancia);
    if (hayDiscrepancia) setShowDiscrepancyModal(true);
  }, [hayDiscrepancia]);
  useEffect(() => {
    setHasDiscrepancy(hayDiscrepancia);
    if (hayDiscrepancia) setShowDiscrepancyModal(true);
  }, [hayDiscrepancia]);

  const turno = closureMeta
    ? {
        id,
        licencia: resolveLicenseLabel(closureMeta.license || closureMeta.licencia),
        driverName:
          driverName ||
          closureMeta.driver?.name ||
          closureMeta.driverName ||
          closureMeta.driver?.fullName ||
          closureMeta.driver?.username ||
          closureMeta.driverId ||
          "Sin nombre",
        facturacion: facturacionElegida,
        tarjeta: Number(closureMeta.cardIncome || 0),
        credito: Number(closureMeta.creditIncome || 0),
        gasolina: Number(closureMeta.gas || 0),
        kmTomar: Number(closureMeta.startKm || 0),
        kmDejar: Number(closureMeta.endKm || 0),
        recaudacionTomar,
        recaudacionDejar,
      }
    : {
        id,
        licencia: "—",
        facturacion: 0,
        tarjeta: 0,
        credito: 0,
        gasolina: 0,
        kmTomar: 0,
        kmDejar: 0,
        recaudacionTomar: 0,
        recaudacionDejar: 0,
        driverName: "Sin nombre",
      };

  const creditManualVal = Number(closureMeta?.creditIncomeManual ?? closureMeta?.creditIncome ?? 0);
  const creditComputedVal = Number(closureMeta?.creditIncomeComputed ?? 0);
  const creditDiscrepancy = Boolean(closureMeta?.creditDiscrepancy);

  const handleResolveCredit = async (source) => {
    try {
      setResolvingCredit(true);
      const updated = await resolveTurnoCredit(id, source);
      setClosureMeta(updated);
      setShowCreditModal(false);
    } catch (err) {
      console.error("Error resolviendo crédito", err);
      setServicesError(err?.message || "No se pudo resolver el crédito");
    } finally {
      setResolvingCredit(false);
    }
  };

  const handleToggleCancelService = async (serviceId, cancelado) => {
    try {
      setMutatingServiceId(serviceId);
      setMutationError("");
      await toggleCancelService(serviceId, cancelado);
      await loadData();
    } catch (err) {
      setMutationError(err?.message || "No se pudo actualizar el servicio");
    } finally {
      setMutatingServiceId(null);
    }
  };

  const deltaKm = turno.kmDejar - turno.kmTomar;
  const efectivo = Math.max(turno.facturacion - turno.tarjeta - turno.credito, 0);
  const neto = turno.facturacion - turno.gasolina;
  const pctForCalc = Number.isFinite(driverPct) ? driverPct : 40;
  const dineroConductor = Number.isFinite(closureMeta?.dineroConductor)
    ? Number(closureMeta.dineroConductor)
    : turno.facturacion * (pctForCalc / 100);
  const balanceEmpresa =
    turno.facturacion - turno.credito - turno.gasolina - turno.tarjeta - dineroConductor;
  const totalConductor = Number.isFinite(closureMeta?.totalConductor)
    ? -Number(closureMeta.totalConductor)
    : -balanceEmpresa;
  const totalEmpresa = Number.isFinite(closureMeta?.totalEmpresa)
    ? -Number(closureMeta.totalEmpresa)
    : balanceEmpresa;

  return (
    <Box
      sx={{
        mx: { xs: 0, md: "auto" },
        px: { xs: 2, md: 2.5 },
        pb: "20px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        boxSizing: "border-box",
        overflowX: "hidden",
        width: "100%",
        maxWidth: { xs: "100%" },
      }}
    >
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{
          color: palette.title,
          textTransform: "none",
          fontSize: "16px",
          fontWeight: 500,
          alignSelf: "flex-start",
          "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
        }}
      >
        Volver
      </Button>

      <Header title={`Detalles del Turno #${id}`} subtitle="Información detallada del turno" />
      <Typography variant="body2" sx={{ color: palette.label, mb: 1 }}>
        Servicios cargados desde backend: {services.length}
      </Typography>
      {hasDiscrepancy && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Typography color="error" variant="body2">
            Facturación por recaudación (€{(facturacionDesdeRecaudacion || 0).toFixed(2)}) no coincide con suma de servicios (€{facturacionDesdeServicios.toFixed(2)}).
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setShowDiscrepancyModal(true)}
            sx={{ textTransform: "none", borderColor: palette.accent, color: palette.text }}
          >
            Ver detalle
          </Button>
        </Stack>
      )}
      <Typography variant="body2" sx={{ color: palette.label, mb: 1 }}>
        Servicios cargados desde backend: {services.length}
      </Typography>
      {hasDiscrepancy && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Typography color="error" variant="body2">
            Facturación por recaudación (€{(facturacionDesdeRecaudacion || 0).toFixed(2)}) no coincide con suma de servicios (€{facturacionDesdeServicios.toFixed(2)}).
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setShowDiscrepancyModal(true)}
            sx={{ textTransform: "none", borderColor: palette.accent, color: palette.text }}
          >
            Ver detalle
          </Button>
        </Stack>
      )}

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DataChip label="Facturación" value={`€${turno.facturacion.toFixed(2)}`} palette={palette} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DataChip label="Tarjeta" value={`€${turno.tarjeta.toFixed(2)}`} palette={palette} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DataChip label="Crédito/Servicios" value={`€${turno.credito.toFixed(2)}`} palette={palette} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DataChip label="Efectivo (calc.)" value={`€${efectivo.toFixed(2)}`} palette={palette} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DataChip label="Gasolina" value={`€${turno.gasolina.toFixed(2)}`} palette={palette} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DataChip label="KM (Δ)" value={`${deltaKm} km`} palette={palette} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DataChip label="Neto (facturación - gasolina)" value={`€${neto.toFixed(2)}`} palette={palette} highlight compact />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DataChip label="Total Conductor" value={`€${totalConductor.toFixed(2)}`} palette={palette} highlight compact />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DataChip label="Total Empresa" value={`€${totalEmpresa.toFixed(2)}`} palette={palette} compact />
        </Grid>
      </Grid>

      {/* Cuerpo: izquierda con scroll propio, derecha con scroll de servicios */}
      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          display: { xs: "block", lg: "grid" },
          gridTemplateColumns: { lg: "1fr 1fr" },
          columnGap: 4,
          alignItems: "stretch",
        }}
      >
        {/* Izquierda: fit en desktop, flujo normal en móvil */}
        {isMobile ? (
          <Stack spacing={2}>
            <InfoCard title="Identificación" palette={palette}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}><RowItem label="Licencia" value={turno.licencia} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="ID de Turno" value={String(turno.id)} palette={palette} /></Grid>
                <Grid item xs={12}><RowItem label="Conductor" value={turno.driverName} palette={palette} /></Grid>
              </Grid>
            </InfoCard>

            <InfoCard title="Kilómetros" palette={palette}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}><RowItem label="Km al Tomar" value={`${turno.kmTomar} km`} palette={palette} /></Grid>
                <Grid item xs={12} sm={4}><RowItem label="Km al Dejar" value={`${turno.kmDejar} km`} palette={palette} /></Grid>
                <Grid item xs={12} sm={4}><RowItem label="Δ Recorrido" value={`${deltaKm} km`} palette={palette} /></Grid>
              </Grid>
            </InfoCard>

            <InfoCard title="Cobros" palette={palette}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={3}><RowItem label="Total Facturado" value={`€${turno.facturacion.toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={3}><RowItem label="Tarjeta" value={`€${turno.tarjeta.toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={3}><RowItem label="Crédito/Servicios" value={`€${turno.credito.toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={3}><RowItem label="Efectivo (calc.)" value={`€${efectivo.toFixed(2)}`} palette={palette} /></Grid>
              </Grid>
            </InfoCard>

            <InfoCard title="Costes y Recaudación" palette={palette}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={3}><RowItem label="Gasolina" value={`€${turno.gasolina.toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={3}><RowItem label="Recaudación al Tomar" value={`€${turno.recaudacionTomar.toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={3}><RowItem label="Recaudación al Dejar" value={`€${turno.recaudacionDejar.toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={3}><RowItem label="Neto (calc.)" value={`€${neto.toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={3}><RowItem label={`% Conductor`} value={`${pctForCalc}%`} palette={palette} /></Grid>
                <Grid item xs={12} sm={3}><RowItem label="Total Conductor" value={`€${totalConductor.toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={3}><RowItem label="Total Empresa" value={`€${totalEmpresa.toFixed(2)}`} palette={palette} /></Grid>
              </Grid>
            </InfoCard>
          </Stack>
        ) : (
          <FitColumn>
            <InfoCard title="Identificación" palette={palette}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}><RowItem label="Licencia" value={turno.licencia} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="ID de Turno" value={String(turno.id)} palette={palette} /></Grid>
                <Grid item xs={12}><RowItem label="Conductor" value={turno.driverName} palette={palette} /></Grid>
              </Grid>
            </InfoCard>

            <InfoCard title="Kilómetros" palette={palette}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}><RowItem label="Km al Tomar" value={`${turno.kmTomar} km`} palette={palette} /></Grid>
                <Grid item xs={12} sm={4}><RowItem label="Km al Dejar" value={`${turno.kmDejar} km`} palette={palette} /></Grid>
                <Grid item xs={12} sm={4}><RowItem label="Δ Recorrido" value={`${deltaKm} km`} palette={palette} /></Grid>
              </Grid>
            </InfoCard>

            <InfoCard title="Cobros" palette={palette}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={3}><RowItem label="Total Facturado" value={`€${turno.facturacion.toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={3}><RowItem label="Tarjeta" value={`€${turno.tarjeta.toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={3}><RowItem label="Crédito/Servicios" value={`€${turno.credito.toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={3}><RowItem label="Efectivo (calc.)" value={`€${efectivo.toFixed(2)}`} palette={palette} /></Grid>
              </Grid>
            </InfoCard>

            <InfoCard title="Costes y Recaudación" palette={palette}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={3}><RowItem label="Gasolina" value={`€${turno.gasolina.toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={3}><RowItem label="Recaudación al Tomar" value={`€${turno.recaudacionTomar.toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={3}><RowItem label="Recaudación al Dejar" value={`€${turno.recaudacionDejar.toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={3}><RowItem label="Neto (calc.)" value={`€${neto.toFixed(2)}`} palette={palette} /></Grid>
              </Grid>
            </InfoCard>
          </FitColumn>
        )}

        {/* Derecha: ocupa el alto restante y tiene su propio scroll */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            minHeight: 0,
            height: { xs: "auto", lg: "100%" }, // llena la fila en desktop
          }}
        >
          <Typography variant="h3" sx={{ color: palette.title, fontWeight: 600, mt: { xs: 1, md: 0 } }}>
            Servicios del Turno
          </Typography>
          {servicesError && (
            <Typography color="error" variant="body2">
              {servicesError}
            </Typography>
          )}
          {mutationError && (
            <Typography color="error" variant="body2">
              {mutationError}
            </Typography>
          )}

          <Box
            sx={{
              overflow: "auto", // solo aquí hay scroll
              minHeight: 0,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              pr: 0.5,
              pb: { xs: 6, md: 2 },
            }}
          >
            {loadingServices && (
              <Typography sx={{ color: palette.label }}>Cargando servicios...</Typography>
            )}
            {!loadingServices && services.length === 0 && !servicesError && (
              <Box
                sx={{
                  backgroundColor: palette.cardBg,
                  border: `1px solid ${palette.cardBorder}`,
                  borderRadius: "12px",
                  p: 2,
                  color: palette.text,
                  mb: 1,
                }}
              >
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>No hay servicios para este turno</Typography>
                <Typography variant="body2" sx={{ color: palette.label }}>
                  No se adjuntaron servicios al crear la hoja de turno. Si creías haberlos cargado, revisa el CSV o añádelos manualmente.
                </Typography>
              </Box>
            )}
            {!loadingServices && services.map((servicio) => {
              const isCancelled = Boolean(servicio.cancelado);
              const totalEuros = Number(servicio["TOTAL EUR"] || 0).toFixed(2);
              return (
                <Box
                  key={servicio.SERVICIO}
                  sx={{
                    backgroundColor: isCancelled ? alpha(palette.cardBg, 0.75) : palette.cardBg,
                    border: `1px solid ${isCancelled ? alpha(palette.cardBorder, 0.6) : palette.cardBorder}`,
                    borderRadius: "12px",
                    p: 2.5,
                    minHeight: 132,
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr auto" },
                    gridTemplateRows: "auto auto",
                    alignItems: "center",
                    rowGap: 1.25,
                    columnGap: 16,
                    boxShadow:
                      palette === PALETTE_LIGHT
                        ? "0 8px 22px rgba(2, 6, 23, 0.08)"
                        : "0 3px 10px rgba(0,0,0,0.24)",
                    opacity: isCancelled ? 0.9 : 1,
                    position: "relative",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ gridColumn: "1 / 2" }}>
                    <Typography sx={{ color: palette.text, fontWeight: 700 }}>
                      Servicio: {servicio.SERVICIO}
                    </Typography>
                    {isCancelled && (
                      <Chip
                        label="Anulado"
                        color="error"
                        size="small"
                        sx={{
                          bgcolor: alpha("#ef4444", 0.12),
                          color: palette.text,
                          border: `1px solid ${alpha("#ef4444", 0.35)}`,
                          fontWeight: 700,
                        }}
                      />
                    )}
                  </Stack>

                  <Chip
                    label={`€${totalEuros}`}
                    sx={{
                      justifySelf: { xs: "start", sm: "end" },
                      gridColumn: { xs: "1 / -1", sm: "2 / 3" },
                      bgcolor: "transparent",
                      border: `1px solid ${palette.accent}`,
                      color: palette.text,
                      fontWeight: 700,
                    }}
                  />

                  <Grid container spacing={1.5} sx={{ gridColumn: "1 / 2", width: "100%" }}>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={{ color: palette.label, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4 }}>
                        Fecha Inicio
                      </Typography>
                      <Typography sx={{ color: palette.text, fontWeight: 600 }}>
                        {servicio["FECHA INICIO"]}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={{ color: palette.label, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4 }}>
                        Método de Pago
                      </Typography>
                      <Typography sx={{ color: palette.text, fontWeight: 600 }}>
                        {servicio["MÉTODO DE PAGO"]}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      gridColumn: { xs: "1 / -1", sm: "2 / 3" },
                      justifySelf: { xs: "stretch", sm: "end" },
                      mt: { xs: 0.5, sm: 0 },
                      width: { xs: "100%", sm: "auto" },
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => navigate(`/servicio/${servicio.SERVICIO}`)}
                      sx={{
                        backgroundColor: palette.btn,
                        color: "white",
                        fontWeight: 600,
                        textTransform: "none",
                        "&:hover": { backgroundColor: palette.btnHover },
                      }}
                    >
                      Ver Detalles
                    </Button>
                    <Button
                      variant={isCancelled ? "outlined" : "contained"}
                      size="small"
                      color={isCancelled ? "primary" : "error"}
                      onClick={() => handleToggleCancelService(servicio.SERVICIO, !isCancelled)}
                      disabled={mutatingServiceId === servicio.SERVICIO}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderColor: isCancelled ? palette.accent : alpha("#ef4444", 0.5),
                        color: palette.text,
                        backgroundColor: isCancelled ? "transparent" : alpha("#ef4444", 0.14),
                        "&:hover": {
                          backgroundColor: isCancelled ? alpha(palette.accent, 0.12) : alpha("#ef4444", 0.22),
                        },
                      }}
                    >
                      {mutatingServiceId === servicio.SERVICIO
                        ? "Guardando..."
                        : isCancelled
                        ? "Reactivar"
                        : "Anular servicio"}
                    </Button>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Dialog
        open={showCreditModal && creditDiscrepancy}
        onClose={() => setShowCreditModal(false)}
        fullWidth
        maxWidth="sm"
        BackdropProps={{
          sx: {
            backgroundColor: alpha("#0F172A", 0.55),
            backdropFilter: "blur(2px)",
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${palette.cardBorder}`,
            backgroundColor: palette.cardBg,
            color: palette.text,
            boxShadow:
              palette === PALETTE_LIGHT
                ? "0 18px 60px rgba(2, 6, 23, 0.12)"
                : "0 20px 70px rgba(0,0,0,0.55)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            pb: 0,
            color: palette.title,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          Revisar crédito de abonados
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            mt: 1,
            pt: 1.5,
            pb: 2.5,
            borderColor: alpha(palette.cardBorder, 0.6),
          }}
        >
          <Stack spacing={1.5}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${palette.cardBorder}`,
                backgroundColor: alpha(palette.accent, isDark ? 0.14 : 0.12),
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: palette.label, textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                Valor introducido
              </Typography>
              <Typography sx={{ fontWeight: 800, color: palette.text, fontSize: "1.1rem" }}>
                €{creditManualVal.toFixed(2)}
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${palette.cardBorder}`,
                backgroundColor: alpha(palette.cardBorder, isDark ? 0.35 : 0.4),
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: palette.label, textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                Valor calculado por servicios abonados
              </Typography>
              <Typography sx={{ fontWeight: 800, color: palette.text, fontSize: "1.1rem" }}>
                €{creditComputedVal.toFixed(2)}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: palette.label }}>
              Elige qué valor usar para los cálculos de la hoja de turno. Solo se mostrará esta alerta mientras exista discrepancia.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2.5,
            display: "flex",
            gap: 1,
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={() => setShowCreditModal(false)}
            disabled={resolvingCredit}
            sx={{
              color: palette.text,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Más tarde
          </Button>
          <Button
            onClick={() => handleResolveCredit("computed")}
            variant="outlined"
            disabled={resolvingCredit}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderColor: palette.accent,
              color: palette.text,
              ":hover": { borderColor: palette.accent, backgroundColor: alpha(palette.accent, 0.12) },
            }}
          >
            Usar calculado (€{creditComputedVal.toFixed(2)})
          </Button>
          <Button
            onClick={() => handleResolveCredit("manual")}
            variant="contained"
            disabled={resolvingCredit}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              backgroundColor: palette.btn,
              ":hover": { backgroundColor: palette.btnHover },
            }}
          >
            Usar introducido (€{creditManualVal.toFixed(2)})
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showDiscrepancyModal && hayDiscrepancia}
        onClose={() => setShowDiscrepancyModal(false)}
        fullWidth
        maxWidth="sm"
        BackdropProps={{
          sx: {
            backgroundColor: alpha("#0F172A", 0.45),
            backdropFilter: "blur(2px)",
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${palette.cardBorder}`,
            backgroundColor: palette.cardBg,
            color: palette.text,
            boxShadow:
              palette === PALETTE_LIGHT
                ? "0 18px 60px rgba(2, 6, 23, 0.12)"
                : "0 20px 70px rgba(0,0,0,0.55)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            pb: 0,
            color: palette.title,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          Diferencia en facturación
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            mt: 1,
            pt: 1.5,
            pb: 2,
            borderColor: alpha(palette.cardBorder, 0.6),
          }}
        >
          <Stack spacing={1.25}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${palette.cardBorder}`,
                backgroundColor: alpha(palette.cardBorder, isDark ? 0.35 : 0.18),
              }}
            >
              <Typography variant="caption" sx={{ color: palette.label, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Por recaudación
              </Typography>
              <Typography sx={{ fontWeight: 800, color: palette.text, fontSize: "1.1rem" }}>
                €{(facturacionDesdeRecaudacion || 0).toFixed(2)}
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${palette.cardBorder}`,
                backgroundColor: alpha(palette.accent, isDark ? 0.15 : 0.12),
              }}
            >
              <Typography variant="caption" sx={{ color: palette.label, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Suma de servicios
              </Typography>
              <Typography sx={{ fontWeight: 800, color: palette.text, fontSize: "1.1rem" }}>
                €{facturacionDesdeServicios.toFixed(2)}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: palette.label }}>
              Los cálculos del turno están usando la facturación por recaudación. Revisa y ajusta los valores si es necesario.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={() => setShowDiscrepancyModal(false)}
            sx={{ textTransform: "none", fontWeight: 700, color: palette.text }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TurnoDetalle;
